/**
 * Public entry point for the SKU Velocity data layer.
 *
 *   import { useSkus, capNumbers, type Sku } from "@/lib/data";
 *
 * The real UKDC snapshot over `/api/data/snapshot` is the DEFAULT; `VITE_DATA_SOURCE=demo` forces
 * the built-in fixtures. Both are `DataProvider` implementations returning plain JSON-serializable
 * data, so no screen changes either way.
 *
 * It used to be opt-IN (`=== "api"`), and that shipped a deployment serving demo figures under a
 * real domain: `import.meta.env` is inlined at BUILD time, the production image is built in a
 * container with no `.env` present, so the variable was simply absent and the flag silently chose
 * fixtures. No runtime setting could override it, and the screen looked fine — the field just
 * described 217 invented SKUs. Opting OUT is the safe direction: a deployment with no snapshot
 * behind it falls back to the fixtures anyway (G-005) and the badge says so, so the worst case of
 * defaulting to `api` is one 404 and the label that was always the point.
 *
 * The api provider falls back to the demo one on 404 / network failure, so a broken backend
 * degrades to a working demo instead of a blank screen.
 */

import { DEMO_LABELS, demoDataProvider } from "./demo";
import type { DataProvider } from "./provider";
import { snapshotDataProvider, snapshotLabels } from "./snapshot";
import type { DataLabels } from "./types";

const USE_API = import.meta.env.VITE_DATA_SOURCE !== "demo";

export const dataProvider: DataProvider = USE_API ? snapshotDataProvider : demoDataProvider;

/** Provenance badge + caption for the current source — see `useDataLabels()`. */
export const dataLabels: () => Promise<DataLabels> = USE_API
  ? snapshotLabels
  : () => Promise.resolve(DEMO_LABELS);

/**
 * Reference tables. `MONTHS` / `YEAR_FACTORS` are static; the ones re-exported from
 * `snapshot.ts` and `served.ts` are MUTABLE singletons seeded with the demo tables and
 * overwritten in place from the snapshot's own `channels` / `geoMarkets` / `calibration` /
 * `source` before `getSkus()` resolves — which is what lets the plot read `FIELD_SCALE`
 * synchronously in `build()` while the snapshot arrives asynchronously. Read them at render
 * time, never cache their contents at module scope.
 */
export { YEAR_FACTORS } from "./demo";
export * from "./hooks";
export * from "./math";
export * from "./money";
export type { DataProvider } from "./provider";
export * from "./scenario";
export { annualisedNote, BASIS, CUR, DEPTH, MONTH_SLOTS, MONTHS, simulated } from "./served";
export { CHANNELS, FIELD_SCALE, GEO_MARKETS, REPORT_META } from "./snapshot";
export * from "./types";
